import {
  AlloggiamentoFiltroDto,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useUpdateAlloggiamentiFiltro,
} from "@/api";
import { SmallLoading } from "@/components/Loading";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Input from "@/elements/Input";
import Table from "@/elements/Table";
import { portaleConfig } from "@/portaliConfig";
import { getFiltroTypes } from "@/utils/filtroTypeUtils";
import {
  IS_NEW_KEY,
  useDbRelationsUpdateForm,
} from "@/utils/useDbRelationsUpdate";
import { withOpeningReset } from "@/utils/withOpeningReset";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Icon,
  MenuItem,
  Select,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, Row, createColumnHelper } from "@tanstack/react-table";
import { countBy } from "lodash";
import { useCallback, useMemo } from "react";
import { Controller } from "react-hook-form";

const columnHelper = createColumnHelper<AlloggiamentoFiltroDto>();

function AlloggiamentiFiltroDialog({
  open = false,
  onClose,
  nodo,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo: NodoDto;
}) {
  const { alloggiamentiFiltro } = nodo;
  const { t, i18n } = useTranslation();

  const handleClose = () => {
    onClose?.();
  };

  const {
    data,
    control,
    resetField,
    watch,
    handleSubmit,
    getChangeSubmitData,
    create,
    remove,
    restore,
    getFieldName,
    getEntityState,
    setValue,
    setError,
    formState: { errors },
    getValues,
  } = useDbRelationsUpdateForm(alloggiamentiFiltro);

  const columns = useMemo<ColumnDef<AlloggiamentoFiltroDto, any>[]>(() => {
    const columns = [
      columnHelper.accessor("nome", {
        header: t("alloggiamentiFiltroDialog.colonne.nome"),
        cell: ({ row }) => (
          <Controller
            name={getFieldName(row.index, "nome")}
            control={control}
            rules={{
              required: t("alloggiamentiFiltroDialog.errori.nomeObbligatorio"),
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <Input fullWidth {...field} />
                  {error && (
                    <FormHelperText error>{error?.message}</FormHelperText>
                  )}
                </>
              );
            }}
          />
        ),
      }),

      columnHelper.accessor("filtroType", {
        header: t("alloggiamentiFiltroDialog.colonne.tipo"),
        cell: ({ row }) => (
          <Controller
            name={getFieldName(row.index, "filtroType")}
            control={control}
            rules={{
              required: t("alloggiamentiFiltroDialog.errori.tipoObbligatorio"),
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <Select {...field} size="small">
                    {getFiltroTypes(i18n.language).map((option) => {
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>

                  {error && (
                    <FormHelperText error>{error?.message}</FormHelperText>
                  )}
                </>
              );
            }}
          />
        ),
      }),

      columnHelper.display({
        id: "buttons",
        header: "",
        size: 125,
        maxSize: 125,
        cell: ({ row }) => {
          return (
            <>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<Icon>delete_outlined</Icon>}
                  onClick={() => {
                    remove(row.index);
                  }}
                >
                  {t("azioni.elimina")}
                </Button>
              </Box>
              <Box
                display="flex"
                gap={1}
                className="deleted-row-visible-content"
              >
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<Icon>restore_from_trash</Icon>}
                  onClick={() => {
                    restore(row.index);
                  }}
                >
                  {t("azioni.ripristina")}
                </Button>
              </Box>
            </>
          );
        },
      }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [control, getFieldName, resetField, watch, remove, restore, i18n.language, t]);

  const isRowDeleted = useCallback(
    (row: Row<AlloggiamentoFiltroDto>) => {
      return getEntityState(row.index) === "deleted";
    },
    [getEntityState]
  );

  const { mutateAsync, isLoading, error } = useUpdateAlloggiamentiFiltro();

  const queryClient = useQueryClient();

  const onSubmit = async () => {
    const data = getValues();
    const occurrencesByName = countBy(data.data, "nome");
    const duplicate = Object.entries(occurrencesByName).find(
      ([, occurrences]) => occurrences > 1
    )?.[0];
    if (duplicate) {
      setError("root", {
        message: t("alloggiamentiFiltroDialog.errori.duplicato", {
          nome: duplicate,
        }),
      });
      return;
    }

    try {
      await mutateAsync({
        id: nodo.id,
        data: getChangeSubmitData(),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
      onClose?.();
    } catch (err) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("alloggiamentiFiltroDialog.titolo", { nome: nodo?.nome })}
      </DialogTitle>
      <DialogContent>
        {alloggiamentiFiltro && (
          <Box>
            <Box>
              <Button
                size="small"
                color="light"
                onClick={() => {
                  create();
                }}
              >
                {t("alloggiamentiFiltroDialog.azioni.aggiungi")}
              </Button>
            </Box>

            {errors.root?.message && (
              <Alert sx={{ mt: 1 }} severity="warning">
                {errors.root?.message}
              </Alert>
            )}

            <Table
              columns={columns}
              data={data}
              sortable={false}
              initialSort={[
                { id: IS_NEW_KEY, desc: false },
                { id: "nome", desc: false },
              ]}
              isRowDeleted={isRowDeleted}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          size="small"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          {t("azioni.salva")}
        </Button>
        {onClose && (
          <Button size="small" color="light" onClick={handleClose}>
            {t("azioni.chiudi")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(AlloggiamentiFiltroDialog);
